import { parseCommand, type Intent, type ProjectRef, type TaskRef } from "@otrack/commands";
import {
  createEvent, dayKey, earnedByCurrency, entryDuration, formatMoney, isLive, isTaskOpen, logEntry, makeId, outstandingByCurrency, overlappingEntries, projectTotals, runningEntry, shiftDays,
  sortCurrencies, startTimer, stopTimer, taskLabel, trackedTime, uninvoicedByCurrency, type Dataset, type MoneyByCurrency, type OperationFactory, type Range, type SyncOperation, type Task
} from "@otrack/domain";
import type { AssistantSource } from "./assistant";
import type { GoogleView } from "./calendar/google";
import { activeProjects, findInvoices, openTasks, plannedEvents, uninvoicedWork } from "./lookups";
import type { Route } from "./router";
import { dayPrefix, dayRange, formatClock, formatDuration, HOUR, MINUTE, monthRange, startOfDay, weekRange } from "./time";

export interface AnswerRow { label: string; value: string; note?: string }
export interface AnswerChoice { label: string; command: string; projectId?: string }

/** One entry of a day, resolved for display. */
export interface DayItem { id: string; projectId: string | null; project: string; client: string | null; description: string; start: number; end: number | null; duration: number; billable: boolean }
/** What one Project earned in a period, in its own currency. */
export interface EarningShare { projectId: string; project: string; client: string | null; tracked: number; amount: number; currency: string }
export type EarningsPeriod = "today" | "week" | "month";

/** Structured data behind an Answer, so it can be drawn instead of listed. */
export type AnswerView =
  | { kind: "day"; from: number; to: number; now: number; total: number; items: DayItem[] }
  | { kind: "earnings"; period: EarningsPeriod; from: number; to: number; tracked: number; shares: EarningShare[] };

export interface Answer {
  title: string;
  text?: string;
  rows?: AnswerRow[];
  /** Follow-up commands the User can pick. */
  choices?: AnswerChoice[];
  /** "problem" when something failed or was refused; plain notices otherwise. */
  tone?: "problem";
  view?: AnswerView;
  /** Written by the assistant in its own words, rather than built by ontrack. */
  reply?: boolean;
  /** Changes already applied while answering. */
  done?: string[];
  /** The records an assistant reply was drawn from, shown under it. */
  sources?: AssistantSource[];
}

export type CommandResult =
  | { type: "operations"; operations: SyncOperation[]; message: string }
  | { type: "answer"; answer: Answer }
  | { type: "navigate"; route: Route }
  /** Not a Fast Path command; the assistant should handle it. */
  | { type: "unknown"; text: string };

/** Live, unarchived Projects with their open Tasks, each most recently used first. */
export function projectRefs(dataset: Dataset): ProjectRef[] {
  const lastUsed = new Map<string, number>();
  for (const entry of Object.values(dataset.timeEntries)) {
    if (!isLive(entry)) continue;
    for (const id of [entry.projectId, entry.taskId]) if (id) lastUsed.set(id, Math.max(lastUsed.get(id) ?? 0, entry.start));
  }
  const recent = (a: { id: string; name: string }, b: { id: string; name: string }) => (lastUsed.get(b.id) ?? 0) - (lastUsed.get(a.id) ?? 0) || a.name.localeCompare(b.name);
  const tasks = new Map<string, TaskRef[]>();
  for (const task of Object.values(dataset.tasks).filter((task) => isTaskOpen(dataset, task)).sort(recent)) {
    tasks.set(task.projectId, [...(tasks.get(task.projectId) ?? []), { id: task.id, name: task.name }]);
  }
  return Object.values(dataset.projects)
    .filter((project) => isLive(project) && project.archivedAt === null)
    .sort(recent)
    .map((project) => {
      const client = project.clientId ? dataset.clients[project.clientId] : undefined;
      return { id: project.id, name: project.name, clientName: client?.name ?? null, tasks: tasks.get(project.id) ?? [] };
    });
}

export function projectLabel(dataset: Dataset, projectId: string | null): string {
  if (!projectId) return "No project";
  const project = dataset.projects[projectId];
  if (!project) return "Unknown project";
  const client = project.clientId ? dataset.clients[project.clientId] : undefined;
  return client ? `${client.name}, ${project.name}` : project.name;
}

/** A Project label with the Task after it: "Lumen Atelier, Checkout rebuild › Refund flow". */
export function targetLabel(dataset: Dataset, projectId: string | null, taskId: string | null): string {
  const task = taskId ? dataset.tasks[taskId] : undefined;
  return task ? `${projectLabel(dataset, projectId)} › ${taskLabel(dataset, task)}` : projectLabel(dataset, projectId);
}

export const EXAMPLES = [
  { label: "start a timer", command: "start " },
  { label: "stop", command: "stop" },
  { label: "log time", command: "worked 1h on " },
  { label: "plan something", command: "plan " },
  { label: "this month's earnings", command: "how much did I earn this month?" }
];

const planDay = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" });

/**
 * Finds the latest free slot of `minutes` that ends no later than the anchor: now for today, or
 * the end of that day's last entry (18:00 if none) for past days.
 */
export function placeDuration(dataset: Dataset, dayOffset: number, minutes: number, now: number): { start: number; end: number } | null {
  const { from, to } = dayRange(now, dayOffset);
  let end = now;
  if (dayOffset !== 0) {
    const ends = Object.values(dataset.timeEntries).filter((e) => isLive(e) && e.start >= from && e.start < to).map((e) => e.end ?? now);
    end = ends.length ? Math.min(to, Math.max(...ends)) : from + 18 * HOUR;
  }
  for (let attempt = 0; attempt < 100; attempt += 1) {
    const start = end - minutes * MINUTE;
    if (start < from) return null;
    const overlaps = overlappingEntries(dataset, start, end, now);
    if (!overlaps.length) return { start, end };
    end = Math.min(...overlaps.map((entry) => entry.start));
  }
  return null;
}

function nameParts(dataset: Dataset, projectId: string | null): { project: string; client: string | null } {
  const project = projectId ? dataset.projects[projectId] : undefined;
  const client = project?.clientId ? dataset.clients[project.clientId] : undefined;
  return { project: project ? project.name : projectId ? "Unknown project" : "No project", client: client?.name ?? null };
}

function earningShares(dataset: Dataset, range: Range, now: number): EarningShare[] {
  return Object.values(dataset.projects)
    .filter(isLive)
    .map((project) => ({ projectId: project.id, ...nameParts(dataset, project.id), ...projectTotals(dataset, project.id, now, range) }))
    .filter((share) => share.earned > 0)
    .sort((a, b) => b.earned - a.earned)
    .map(({ projectId, project, client, tracked, earned, currency }) => ({ projectId, project, client, tracked, amount: earned, currency }));
}

function moneyRows(totals: Record<string, number>): AnswerRow[] {
  return Object.entries(totals)
    .sort(([a], [b]) => (a === "USD" ? -1 : b === "USD" ? 1 : a.localeCompare(b)))
    .map(([currency, amount]) => ({ label: currency, value: formatMoney({ amount, currency }) }));
}

/** "$1,200.00 and €300.00" */
function moneyText(money: MoneyByCurrency): string {
  const parts = sortCurrencies(Object.keys(money)).filter((currency) => money[currency]).map((currency) => formatMoney({ amount: money[currency]!, currency }));
  return parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}` : parts[0] ?? "";
}

const plural = (count: number, noun: string, many = `${noun}s`) => `${count} ${count === 1 ? noun : many}`;

function tasksAnswer(dataset: Dataset, projectId: string | null, now: number): Answer {
  const source = openTasks(dataset, now, projectId ?? undefined);
  const projects = new Set(source.items.map((item) => item.projectId)).size;
  const title = projectId ? `Open tasks, ${projectLabel(dataset, projectId)}` : "Open tasks";
  if (!source.total) return { title, text: projectId ? `${projectLabel(dataset, projectId)} has no open tasks.` : "No open tasks. Everything is done." };
  const text = projectId ? `${plural(source.total, "open task")} on ${projectLabel(dataset, projectId)}.` : `${plural(source.total, "open task")} across ${plural(projects, "project")}.`;
  return { title, text, sources: [source] };
}

function projectsAnswer(dataset: Dataset, now: number): Answer {
  const source = activeProjects(dataset, now);
  if (!source.total) return { title: "Projects", text: "No projects in progress." };
  return { title: "Projects", text: `${plural(source.total, "project")} in progress.`, sources: [source] };
}

function invoicesAnswer(dataset: Dataset, status: "unpaid" | "overdue" | "draft" | null, now: number): Answer {
  const invoices = findInvoices(dataset, now, status ?? undefined);
  const source: AssistantSource = { kind: "invoices", ids: invoices.slice(0, 50).map((invoice) => invoice.id), total: invoices.length };
  const noun = { unpaid: "unpaid invoice", overdue: "overdue invoice", draft: "draft invoice" }[status ?? "unpaid"];
  const title = status === "overdue" ? "Overdue invoices" : status === "draft" ? "Draft invoices" : status ? "Unpaid invoices" : "Invoices";
  if (!invoices.length) return { title, text: status ? `No ${noun}s.` : "No invoices yet." };
  const owed = status === "unpaid" ? outstandingByCurrency(dataset) : status === "overdue" ? outstandingByCurrency(dataset, now) : {};
  const amount = moneyText(owed);
  return { title, text: status ? `${plural(invoices.length, noun)}${amount ? `, ${amount} ${status === "overdue" ? "past due" : "outstanding"}` : ""}.` : `${plural(invoices.length, "invoice")}.`, sources: [source] };
}

function uninvoicedAnswer(dataset: Dataset, now: number): Answer {
  const source = uninvoicedWork(dataset, now);
  if (!source.clients.length) return { title: "Not invoiced yet", text: "Everything earned is on an invoice." };
  return { title: "Not invoiced yet", text: `${moneyText(uninvoicedByCurrency(dataset))} not invoiced yet, from ${plural(source.clients.length, "client")}.`, sources: [source] };
}

/** How far ahead "upcoming" looks. */
const UPCOMING_DAYS = 14;

/** "today at 10:00", "tomorrow, all day", "Mon, Oct 26 at 9:30". */
function whenAhead(start: number, allDay: boolean, now: number): string {
  const day = startOfDay(start);
  const name = day === startOfDay(now) ? "today" : day === startOfDay(now, 1) ? "tomorrow" : planDay.format(day);
  return allDay ? `${name}, all day` : `${name} at ${formatClock(start)}`;
}

function eventsAnswer(dataset: Dataset, now: number, google?: GoogleView): Answer {
  const source = plannedEvents(dataset, now, startOfDay(now, UPCOMING_DAYS + 1), now, google);
  const title = "Upcoming events";
  if (!source.total) return { title, text: "Nothing planned in the next two weeks.", choices: [{ label: "Plan something", command: "plan " }] };
  const next = source.items[0]!;
  const lead = next.start <= now && !next.allDay ? `Now: ${next.title}, until ${formatClock(next.end)}.` : `Next: ${next.title}, ${whenAhead(next.start, next.allDay, now)}.`;
  return { title, text: `${plural(source.total, "event")} in the next two weeks. ${lead}`, sources: [source] };
}

/** A Project's open Task called `name`, ignoring case. */
export function openTaskNamed(dataset: Dataset, projectId: string, name: string): Task | undefined {
  return Object.values(dataset.tasks).find((task) => task.projectId === projectId && isTaskOpen(dataset, task) && task.name.toLowerCase() === name.toLowerCase());
}

/**
 * The Task a command asked to create: an open Task of the Project already called that, or a new
 * one, so saying it twice never makes two.
 */
function taskNamed(dataset: Dataset, projectId: string, name: string, factory: OperationFactory): { taskId: string; label: string; operations: SyncOperation[] } {
  const existing = openTaskNamed(dataset, projectId, name);
  if (existing) return { taskId: existing.id, label: targetLabel(dataset, projectId, existing.id), operations: [] };
  const taskId = makeId("task");
  return { taskId, label: `${projectLabel(dataset, projectId)} › ${name} (new task)`, operations: [factory.op("task", taskId, { projectId, name })] };
}

/** Asks which Project to run the command on, offering the most recent ones. */
function pickProject(projects: ProjectRef[], text: string, purpose: string): CommandResult {
  return { type: "answer", answer: { title: "Which project?", text: `Pick one to ${purpose}.`, choices: projects.slice(0, 4).map((p) => ({ label: p.clientName ? `${p.clientName}, ${p.name}` : p.name, command: `${text} on ${p.name}`, projectId: p.id })) } };
}

/** Runs a quick command. `google` adds Google Calendar events to what "upcoming events" lists. */
export function executeCommand(text: string, dataset: Dataset, factory: OperationFactory, now: number, google?: GoogleView): CommandResult {
  const projects = projectRefs(dataset);
  const intent: Intent = parseCommand(text, { projects });
  switch (intent.kind) {
    case "stop": {
      const running = runningEntry(dataset);
      if (!running) return { type: "answer", answer: { title: "Nothing to stop", text: "No timer is running." } };
      return { type: "operations", operations: stopTimer(dataset, factory, now), message: `Stopped ${targetLabel(dataset, running.projectId, running.taskId)} at ${formatDuration(entryDuration(running, now))}` };
    }
    case "start": {
      const running = runningEntry(dataset);
      if (intent.newTask !== undefined && !intent.projectId) return pickProject(projects, text, "add this task to");
      const task = intent.newTask !== undefined && intent.projectId ? taskNamed(dataset, intent.projectId, intent.newTask, factory) : null;
      const label = task ? task.label : intent.projectId ? targetLabel(dataset, intent.projectId, intent.taskId) : "a timer without a project";
      return {
        type: "operations",
        operations: [...task?.operations ?? [], ...startTimer(dataset, { projectId: intent.projectId, taskId: task?.taskId ?? intent.taskId, description: intent.description }, factory, now)],
        message: running ? `Switched to ${label}` : `Started ${label}`
      };
    }
    case "log": {
      if (!intent.projectId) return pickProject(projects, text, intent.newTask !== undefined ? "add this task to" : "log this time on");
      let slot: { start: number; end: number } | null;
      if ("minutes" in intent) {
        slot = placeDuration(dataset, intent.dayOffset, intent.minutes, now);
        if (!slot) return { type: "answer", answer: { title: "No room for that", tone: "problem", text: `There is no free ${formatDuration(intent.minutes * MINUTE)} gap ${intent.dayOffset ? "that day" : "earlier today"}. Try a time range, like "9-11".` } };
      } else {
        const day = startOfDay(now, intent.dayOffset);
        slot = { start: day + intent.startMinute * MINUTE, end: day + intent.endMinute * MINUTE };
        if (slot.end > now) return { type: "answer", answer: { title: "That is in the future", tone: "problem", text: "Time can only be logged up to now. Start a timer instead." } };
        const overlaps = overlappingEntries(dataset, slot.start, slot.end, now);
        if (overlaps.length) return { type: "answer", answer: { title: "That overlaps other time", tone: "problem", text: `It overlaps ${projectLabel(dataset, overlaps[0]!.projectId)} (${formatClock(overlaps[0]!.start)} to ${overlaps[0]!.end ? formatClock(overlaps[0]!.end) : "now"}).` } };
      }
      const task = intent.newTask !== undefined ? taskNamed(dataset, intent.projectId, intent.newTask, factory) : null;
      return {
        type: "operations",
        operations: [...task?.operations ?? [], ...logEntry(dataset, { projectId: intent.projectId, taskId: task?.taskId ?? intent.taskId, description: intent.description }, slot.start, slot.end, factory)],
        message: `Logged ${formatDuration(slot.end - slot.start)} on ${task ? task.label : targetLabel(dataset, intent.projectId, intent.taskId)}, ${dayPrefix(slot.start, now)}${formatClock(slot.start)} to ${formatClock(slot.end)}`
      };
    }
    case "plan": {
      const today = startOfDay(now);
      const day = "offset" in intent.day ? startOfDay(today, intent.day.offset) : startOfDay(today, (intent.day.weekday - new Date(today).getDay() + 7) % 7);
      const title = intent.description || (intent.taskId ? dataset.tasks[intent.taskId]?.name ?? "" : "");
      const allDay = intent.startMinute === null || intent.endMinute === null;
      const start = allDay ? day : new Date(day).setHours(0, intent.startMinute!, 0, 0);
      const end = allDay ? shiftDays(day, 1) : new Date(day).setHours(0, intent.endMinute!, 0, 0);
      const created = createEvent({ title, notes: "", projectId: intent.projectId, taskId: intent.taskId, allDay, start, end, startDay: allDay ? dayKey(day) : null, endDay: allDay ? dayKey(day) : null, repeat: null }, factory);
      const what = title || (intent.projectId ? projectLabel(dataset, intent.projectId) : "an event");
      const when = day === today ? "today" : day === startOfDay(today, 1) ? "tomorrow" : planDay.format(day);
      return { type: "operations", operations: created.operations, message: `Planned ${what}, ${when}${allDay ? "" : ` ${formatClock(start)} to ${formatClock(end)}`}` };
    }
    case "calendar": {
      const view = intent.view ?? "week";
      const today = new Date(startOfDay(now));
      let day = today.getTime();
      if (intent.month !== null) day = new Date(today.getFullYear(), intent.month, 1).getTime();
      else if (view === "month") day = new Date(today.getFullYear(), today.getMonth() + intent.offset, 1).getTime();
      else day = startOfDay(day, intent.offset * (view === "week" ? 7 : 1));
      return { type: "navigate", route: { page: "calendar", view: intent.view === null && intent.offset === 0 && intent.month === null ? null : view, date: dayKey(day) } };
    }
    case "earnings": {
      const range = intent.period === "today" ? dayRange(now) : intent.period === "week" ? weekRange(now) : monthRange(now);
      const rows = moneyRows(earnedByCurrency(dataset, range, now));
      const title = intent.period === "today" ? "Earned today" : intent.period === "week" ? "Earned this week" : "Earned this month";
      const view: AnswerView = { kind: "earnings", period: intent.period, ...range, tracked: trackedTime(dataset, range, now).total, shares: earningShares(dataset, range, now) };
      return { type: "answer", answer: rows.length ? { title, rows, view } : { title, text: "Nothing billable yet.", view } };
    }
    case "today": {
      const { from, to } = dayRange(now);
      const items: DayItem[] = Object.values(dataset.timeEntries)
        .filter((e) => isLive(e) && e.start >= from && e.start < to)
        .sort((a, b) => a.start - b.start)
        .map((e) => ({ id: e.id, projectId: e.projectId, ...nameParts(dataset, e.projectId), description: e.description, start: e.start, end: e.end, duration: entryDuration(e, now), billable: e.billable }));
      if (!items.length) return { type: "answer", answer: { title: "Today", text: "Nothing tracked yet today.", choices: [{ label: "Start a timer", command: "start " }, { label: "Log time", command: "worked 1h on " }] } };
      const total = items.reduce((sum, item) => sum + item.duration, 0);
      return { type: "answer", answer: { title: "Today", view: { kind: "day", from, to, now, total, items } } };
    }
    case "tasks":
      return { type: "answer", answer: tasksAnswer(dataset, intent.projectId, now) };
    case "projects":
      return { type: "answer", answer: projectsAnswer(dataset, now) };
    case "invoices":
      return { type: "answer", answer: invoicesAnswer(dataset, intent.status, now) };
    case "uninvoiced":
      return { type: "answer", answer: uninvoicedAnswer(dataset, now) };
    case "events":
      return { type: "answer", answer: eventsAnswer(dataset, now, google) };
    case "unknown":
      return { type: "unknown", text };
  }
}
